import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { statusChipClass, STATUS_APPROVED, STATUS_PENDING, STATUS_REJECTED } from './changeRequestFormat';
import ChangeRequestModal from './ChangeRequestModal';
import ChangeRequestsTable from './ChangeRequestsTable';
import ChangeRequestsTiles from './ChangeRequestsTiles';
import RecordedChangesTable from './RecordedChangesTable';
import StatusSegment from './StatusSegment';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import DateRangePickerField from 'CommonComponents/DateRangePickerField';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import MultiSelectField from 'CommonComponents/MultiSelectField';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

import FileExportIcon from 'Assets/icons/file-export.svg';

const RANGE_BUDGET = 'budget';
const RANGE_CUSTOM = 'custom';

const EMPTY_TILES = {
    pending_count: 0,
    pending_value: 0,
    local_balance: null,
    central_balance: null,
    total_balance: null,
    balance_after_pending: null,
    event_profit_after_pending: 0,
};

const EMPTY_COUNTS = { all: 0, pending: 0, approved: 0, rejected: 0 };

const LOG_STATUSES = [
    { value: 'all', label: () => $t('All') },
    { value: 'pending', label: () => $t('Pending') },
    { value: 'approved', label: () => $t('Approved') },
    { value: 'rejected', label: () => $t('Rejected') },
    { value: 'applied', label: () => $t('No approver set up') },
];

const EMPTY_META = { current_page: 1, last_page: 1, total: 0, per_page: 50 };

/**
 * The Budget Change Requests report, rendered identically on a location's own tab
 * and in Report Centre — the tab IS this report pre-filtered to one location, so
 * there is one layout to learn and one code path to keep right.
 *
 * `scoped` is a location's own log: every change recorded there, applied ones
 * included, with its own filters and columns. It needs no Department / cost centre
 * column or filter, because every row names that location.
 */
export default function ChangeRequestsReport({ endpoint, exportUrl, scoped = false, locationType = null, budgetChangesUrl = null }) {
    const toast = useToast();
    const detailModal = useModal();

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(EMPTY_META);
    const [tiles, setTiles] = useState(EMPTY_TILES);
    const [options, setOptions] = useState({ statuses: EMPTY_COUNTS, departments: [], users: [], groups: [], lines: [], types: [], categories: [] });
    const [recordingAll, setRecordingAll] = useState(false);

    const [status, setStatus] = useState('all');
    const [range, setRange] = useState(RANGE_BUDGET);
    const [dates, setDates] = useState([null, null]);
    const [locations, setLocations] = useState([]);
    const [group, setGroup] = useState('all');
    const [requestedBy, setRequestedBy] = useState('');
    const [line, setLine] = useState('');
    const [type, setType] = useState('');
    const [category, setCategory] = useState('');
    const [page, setPage] = useState(1);

    const [selected, setSelected] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [failed, setFailed] = useState(false);

    const params = useMemo(() => {
        const query = new URLSearchParams({ status, range, page: String(page) });

        if (range === RANGE_CUSTOM) {
            if (dates[0]) {
                query.set('from', dates[0]);
            }
            if (dates[1]) {
                query.set('to', dates[1]);
            }
        }

        if (group !== 'all') {
            query.set('group', group);
        }

        if (requestedBy) {
            query.set('requested_by', requestedBy);
        }

        if (line) {
            query.set('line', line);
        }

        if (type) {
            query.set('type', type);
        }

        if (category) {
            query.set('category', category);
        }

        locations.forEach((location) => query.append('locations[]', location));

        return query;
    }, [status, range, dates, group, requestedBy, line, type, category, locations, page]);

    const fetchReport = useCallback(() => {
        setLoading(true);
        setFailed(false);

        return (
            axios
                .get(`${endpoint}${endpoint.includes('?') ? '&' : '?'}${params.toString()}`)
                .then((res) => {
                    setRows(res.data.rows || []);
                    // A functional update, so the fallback does not have to close over `meta`
                    // and go stale between renders.
                    setMeta((current) => res.data.meta || current);
                    setTiles(res.data.tiles || EMPTY_TILES);
                    setOptions({
                        statuses: res.data.filter_options?.statuses || EMPTY_COUNTS,
                        departments: res.data.filter_options?.departments || [],
                        users: res.data.filter_options?.users || [],
                        groups: res.data.filter_options?.groups || [],
                        lines: res.data.filter_options?.lines || [],
                        types: res.data.filter_options?.types || [],
                        categories: res.data.filter_options?.categories || [],
                    });
                    setRecordingAll(Boolean(res.data.record_all?.enabled));
                })
                // Without this a 403 or a stale location id in a bookmarked URL left the
                // initial empty state on screen, and the table read "No budget change
                // requests for these filters" — a failure indistinguishable from an empty
                // report, plus an unhandled rejection in Sentry.
                //
                // The tiles and the pagination line are cleared alongside the rows: the
                // table hides behind the error alert but they do not, so a failed reload
                // otherwise left last-known balances and "1–50 of 217" sitting above it,
                // reading as current.
                .catch((error) => {
                    setRows([]);
                    setTiles(EMPTY_TILES);
                    setMeta(EMPTY_META);
                    setFailed(true);
                    toast.error(error.response?.data?.message || $t('This report could not be loaded.'));
                })
                .finally(() => setLoading(false))
        );
    }, [endpoint, params, toast]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    // A link to one entry of this location's log (from a contingency movement) opens its
    // detail. Read once; the parameter is dropped from the address either way so a
    // reload or a shared link does not reopen it.
    useEffect(() => {
        const url = new URL(window.location.href);
        const requestId = url.searchParams.get('request');

        if (!requestId) {
            return;
        }

        url.searchParams.delete('request');
        window.history.replaceState(window.history.state, '', url.toString());

        if (!scoped) {
            return;
        }

        const query = new URLSearchParams({ request: requestId, status: 'all', per_page: '1' });

        axios
            .get(`${endpoint}${endpoint.includes('?') ? '&' : '?'}${query.toString()}`)
            .then((res) => {
                const row = res.data.rows?.[0];

                if (row) {
                    setSelected(row);
                    detailModal.show();
                }
            })
            .catch(() => toast.error($t('This budget change could not be opened.')));
    }, []);

    const resetToFirstPage = (setter) => (value) => {
        setPage(1);
        setter(value);
    };

    const openDetails = (row) => {
        setSelected(row);
        detailModal.show();
    };

    const deleteRequest = (row) => {
        if (!row.delete_url || !confirm($t('Delete this budget change request?'))) {
            return;
        }

        setDeletingId(row.id);

        axios
            .delete(row.delete_url)
            // Deleting a pending request changes what every surviving request can draw
            // on, so the whole report is re-read rather than the row spliced out.
            .then(() => fetchReport())
            .catch((error) => toast.error(error.response?.data?.errors || $t('This request could not be deleted.')))
            .finally(() => setDeletingId(null));
    };

    // Cost centres are indented under their department, so one control covers both
    // levels without a second shape to keep in step.
    const locationOptions = useMemo(
        () =>
            options.departments.map((option) => ({
                value: `${option.type}:${option.id}`,
                label: option.name,
                level: option.parent_id ? 1 : 0,
            })),
        [options.departments],
    );

    // Position in the whole report, not in the page that happens to be loaded.
    const rangeFrom = meta.total === 0 ? 0 : (meta.current_page - 1) * meta.per_page + 1;
    const rangeTo = Math.min(meta.current_page * meta.per_page, meta.total);

    return (
        <div className="table__card tw-flex tw-flex-col tw-gap-6">
            <ChangeRequestsTiles tiles={tiles} scoped={scoped} />

            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2.5">
                {scoped ? (
                    <>
                        <label className="bc-filter tw-mb-0">
                            {$t('Status')}:
                            <select
                                className="bc-filter__select"
                                value={status}
                                aria-label={$t('Status')}
                                onChange={(e) => resetToFirstPage(setStatus)(e.target.value)}
                            >
                                {LOG_STATUSES.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label()} ({options.statuses[option.value] ?? 0})
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="bc-filter tw-mb-0">
                            {$t('Changed by')}:
                            <select
                                className="bc-filter__select"
                                value={requestedBy}
                                aria-label={$t('Changed by')}
                                onChange={(e) => resetToFirstPage(setRequestedBy)(e.target.value)}
                            >
                                <option value="">{$t('All')}</option>
                                {options.users.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="bc-filter tw-mb-0">
                            {$t('Line')}:
                            <select
                                className="bc-filter__select"
                                value={line}
                                aria-label={$t('Line')}
                                onChange={(e) => resetToFirstPage(setLine)(e.target.value)}
                            >
                                <option value="">{$t('All')}</option>
                                {options.lines.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="bc-filter tw-mb-0">
                            {$t('Type')}:
                            <select
                                className="bc-filter__select"
                                value={type}
                                aria-label={$t('Type')}
                                onChange={(e) => resetToFirstPage(setType)(e.target.value)}
                            >
                                <option value="">{$t('All')}</option>
                                {options.types.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <div className="tw-flex tw-items-center tw-gap-1.5">
                            <span className={statusChipClass(STATUS_PENDING)}>{$t('Pending')}</span>
                            <span className={statusChipClass(STATUS_APPROVED)}>{$t('Approved')}</span>
                            <span className={statusChipClass(STATUS_REJECTED)}>{$t('Rejected')}</span>
                            <span className="bc-chip bc-chip--grey">{$t('No approver set up')}</span>
                        </div>
                    </>
                ) : (
                    <StatusSegment value={status} counts={options.statuses} onChange={resetToFirstPage(setStatus)} />
                )}

                {!scoped && (
                    <>
                        <MultiSelectField
                            className="tw-min-w-[240px]"
                            options={locationOptions}
                            value={locations}
                            showIndentation
                            clearable
                            placeholder={$t('Department / cost centre')}
                            onChange={(value) => resetToFirstPage(setLocations)((value || []).map((option) => option.value))}
                        />

                        <label className="bc-filter tw-mb-0">
                            {$t('Group')}:
                            <select
                                className="bc-filter__select"
                                value={group}
                                aria-label={$t('Group')}
                                onChange={(e) => resetToFirstPage(setGroup)(e.target.value)}
                            >
                                <option value="all">{$t('All groups')}</option>
                                {options.groups.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="bc-filter tw-mb-0">
                            {$t('Requested by')}:
                            <select
                                className="bc-filter__select"
                                value={requestedBy}
                                aria-label={$t('Requested by')}
                                onChange={(e) => resetToFirstPage(setRequestedBy)(e.target.value)}
                            >
                                <option value="">{$t('Anyone')}</option>
                                {options.users.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </>
                )}

                <label className="bc-filter tw-mb-0">
                    {$t('Category')}:
                    <select
                        className="bc-filter__select"
                        value={category}
                        aria-label={$t('Category')}
                        onChange={(e) => resetToFirstPage(setCategory)(e.target.value)}
                    >
                        <option value="">{$t('All')}</option>
                        {options.categories.map((option) => (
                            <option key={option.id} value={option.id}>
                                {option.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="bc-filter tw-mb-0">
                    {$t('Date range')}:
                    <select
                        className="bc-filter__select"
                        value={range}
                        aria-label={$t('Date range')}
                        onChange={(e) => resetToFirstPage(setRange)(e.target.value)}
                    >
                        <option value={RANGE_BUDGET}>{$t('This budget version')}</option>
                        <option value={RANGE_CUSTOM}>{$t('Custom range')}</option>
                    </select>
                </label>

                {range === RANGE_CUSTOM && (
                    <div className="[&_.react-datepicker-popper]:tw-z-20">
                        <label className="tw-sr-only" htmlFor="changeRequestsDateRange">
                            {$t('Custom date range')}
                        </label>
                        <DateRangePickerField
                            id="changeRequestsDateRange"
                            startDate={dates[0]}
                            endDate={dates[1]}
                            valueFormat="yyyy-MM-dd"
                            dateFormat="yyyy-MM-dd"
                            placeholderText={$t('Select dates')}
                            className="bc-input tw-text-start"
                            onChange={(value) => resetToFirstPage(setDates)(value)}
                        />
                    </div>
                )}

                <div className="tw-grow" />

                <Button variant="outline" small as="a" href={`${exportUrl}${exportUrl.includes('?') ? '&' : '?'}${params.toString()}`} data-action="navigate">
                    <FileExportIcon className="tw-w-3 tw-h-3" />
                    {$t('Export CSV')}
                </Button>
            </div>

            <div className="tw-flex tw-items-center tw-space-x-3">
                <PaginationInfo from={rangeFrom} to={rangeTo} total={meta.total} />
                <TablePagination lastPage={meta.last_page} currentPage={meta.current_page} pageChange={(data) => setPage(data.selected + 1)} />
            </div>

            {loading && <LoadingSpinner />}

            {!loading && failed && (
                <Alert type="error">{$t('This report could not be loaded. Refresh the page, or check that you still have access to this location.')}</Alert>
            )}

            {!loading && !failed && scoped && (
                <RecordedChangesTable
                    rows={rows}
                    emptyText={
                        {
                            costcenter: () => $t('No changes recorded for this cost centre.'),
                            revdepartment: () => $t('No changes recorded for this revenue department.'),
                            revcostcenter: () => $t('No changes recorded for this revenue cost centre.'),
                        }[locationType]?.() ?? $t('No changes recorded for this department.')
                    }
                    emptyHint={
                        !recordingAll ? (
                            budgetChangesUrl ? (
                                <a className="bc-link" href={budgetChangesUrl}>
                                    {$t('Switch on Record all budget changes on the Budget Changes page to log every change from that point on.')}
                                </a>
                            ) : (
                                $t('Switch on Record all budget changes on the Budget Changes page to log every change from that point on.')
                            )
                        ) : null
                    }
                    onOpen={openDetails}
                    onDelete={deleteRequest}
                    deletingId={deletingId}
                />
            )}

            {!loading && !failed && !scoped && (
                <ChangeRequestsTable rows={rows} showLocation onOpen={openDetails} onDelete={deleteRequest} deletingId={deletingId} />
            )}

            <div className="bc-hint">
                <span className="bc-hint__icon" aria-hidden="true">
                    &#9432;
                </span>
                {$t(
                    'Selecting a row opens the request details. Requests can be deleted by the requester while pending; approved and rejected requests are read-only. Approvals are actioned from the approver’s home page, not from this report.',
                )}
            </div>

            <ChangeRequestModal modal={detailModal} row={selected} profitEnabled={!!meta.profit_enabled} />
        </div>
    );
}
